import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { fetchWithAuth } from '../services/api';
import { 
  IdentificationIcon, 
  ShoppingBagIcon, 
  QrCodeIcon,
  ChartBarIcon,
  CalendarIcon,
  UsersIcon,
  ClockIcon,
  EyeIcon,
  ChatBubbleLeftEllipsisIcon,
  FlagIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
  BoltIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const AdminManagement = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [notificationMetrics, setNotificationMetrics] = useState(null);
  const [totalCounts, setTotalCounts] = useState({ profiles: 0, users: 0, orders: 0, contactMessages: 0 });

  useEffect(() => {
    const checkAdminAccess = async () => {
      const token = localStorage.getItem('jwtToken');
      const role = localStorage.getItem('userRole');

      if (!token || role !== 'ADMIN') {
        navigate('/');
        return;
      }

      try {
        const data = await fetchWithAuth('/admin/test');
        setTestResult(data);
        
        // Fetch admin stats
        const statsData = await fetchWithAuth('/admin/stats');
        setStats(statsData);

        // Fetch total counts
        const [profilesData, usersData, ordersData, contactMessagesData, notificationsData] = await Promise.all([
          fetchWithAuth('/admin/profiles'),
          fetchWithAuth('/admin/users'),
          fetchWithAuth('/admin/orders'),
          fetchWithAuth('/admin/contact-messages'),
          fetchWithAuth('/admin/notifications').catch(() => null)
        ]);

        setNotificationMetrics(notificationsData);

        setTotalCounts({
          profiles: Array.isArray(profilesData) ? profilesData.length : 0,
          users: Array.isArray(usersData) ? usersData.length : 0,
          orders: Array.isArray(ordersData) ? ordersData.length : 0,
          contactMessages: Array.isArray(contactMessagesData) ? contactMessagesData.length : 0
        });
      } catch (err) {
        setError('Error checking admin access');
        console.error('Admin access error:', err);
      } finally {
        setStatsLoading(false);
      }
    };

    checkAdminAccess();
  }, [navigate]);

  const CombinedStatCard = ({ title, totalValue, icon: Icon, todayValue, weekValue, monthValue, gradient, to, testId }) => (
    <Link
      to={to}
      className={`block bg-gradient-to-r ${gradient} text-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] focus:outline-none focus:ring-4 focus:ring-indigo-300`}
      data-testid={testId}
      aria-label={`${title}: ${totalValue}`}
    >
      {/* Header with total */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-white opacity-90">{title}</h3>
          <p className="text-4xl font-bold text-white mt-1">{totalValue.toLocaleString()}</p>
        </div>
        <Icon className="h-12 w-12 text-white opacity-80" />
      </div>
      
      {/* Recent activity */}
      <div className="space-y-2 pt-4 border-t border-white border-opacity-20">
        <div className="flex justify-between items-center">
          <span className="text-sm text-white opacity-80 flex items-center">
            <ClockIcon className="h-3 w-3 mr-1" />
            Денес
          </span>
          <span className="text-xl font-bold text-white">+{todayValue}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-white opacity-80 flex items-center">
            <CalendarIcon className="h-3 w-3 mr-1" />
            Оваа недела
          </span>
          <span className="text-xl font-bold text-white">+{weekValue}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-white opacity-80 flex items-center">
            <CalendarIcon className="h-3 w-3 mr-1" />
            Овој месец
          </span>
          <span className="text-xl font-bold text-white">+{monthValue}</span>
        </div>
      </div>
    </Link>
  );

  const NotificationAdminCard = () => {
    const activeEvents = notificationMetrics?.events?.filter(event => event.enabled).length || 0;
    const totalEvents = notificationMetrics?.events?.length || 0;
    const ready = notificationMetrics?.configured || notificationMetrics?.testMode;
    return (
      <Link
        to={`/${lang}/admin/telegram-config`}
        className="group relative block overflow-hidden rounded-2xl bg-gradient-to-r from-slate-950 via-slate-900 to-cyan-950 p-6 text-white shadow-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl focus:outline-none focus:ring-4 focus:ring-cyan-300 md:p-8"
        data-testid="admin-telegram-config-link"
      >
        <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-cyan-400/10 blur-3xl transition group-hover:bg-cyan-400/20" />
        <div className="relative flex flex-col gap-7 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-4">
            <div className="rounded-2xl bg-cyan-400/15 p-3 ring-1 ring-cyan-300/20"><PaperAirplaneIcon className="h-8 w-8 text-cyan-300" /></div>
            <div>
              <div className="flex flex-wrap items-center gap-2"><h3 className="text-2xl font-bold">Telegram & Notifications</h3><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${ready ? 'bg-emerald-400/15 text-emerald-300' : 'bg-rose-400/15 text-rose-300'}`}>{notificationMetrics?.configured ? 'Connected' : notificationMetrics?.testMode ? 'Simulation' : 'Needs setup'}</span></div>
              <p className="mt-2 max-w-xl text-sm text-slate-300">Control event routing, test enabled templates, and monitor Telegram delivery pressure.</p>
              <p className="mt-4 text-sm font-semibold text-cyan-300 group-hover:text-cyan-200">Open notification control center →</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:min-w-[500px]">
            <div className="rounded-xl bg-white/5 p-3 ring-1 ring-white/10"><PaperAirplaneIcon className="h-4 w-4 text-cyan-300" /><p className="mt-2 text-2xl font-bold">{notificationMetrics?.sentToday || 0}</p><p className="text-xs text-slate-400">Today</p></div>
            <div className="rounded-xl bg-white/5 p-3 ring-1 ring-white/10"><BoltIcon className="h-4 w-4 text-amber-300" /><p className="mt-2 text-2xl font-bold">{notificationMetrics?.sentLastMinute || 0}</p><p className="text-xs text-slate-400">Last minute</p></div>
            <div className="rounded-xl bg-white/5 p-3 ring-1 ring-white/10"><ExclamationTriangleIcon className="h-4 w-4 text-rose-300" /><p className="mt-2 text-2xl font-bold">{notificationMetrics?.failedToday || 0}</p><p className="text-xs text-slate-400">Failed</p></div>
            <div className="rounded-xl bg-white/5 p-3 ring-1 ring-white/10"><CheckCircleIcon className="h-4 w-4 text-emerald-300" /><p className="mt-2 text-2xl font-bold">{activeEvents}/{totalEvents}</p><p className="text-xs text-slate-400">Active events</p></div>
          </div>
        </div>
      </Link>
    );
  };

  if (error) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-gray-50 py-8">
        <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-lg">
          <h2 className="text-2xl font-bold text-red-600 mb-4">{t('adminManagement.error', 'Access Error')}</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="text-center mb-12">
          <h1 
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
            data-testid="admin-management-title"
          >
            {t('adminManagement.title', 'Admin Management')}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('adminManagement.subtitle', 'Monitor and manage your platform with comprehensive analytics and tools')}
          </p>
        </div>

        {testResult ? (
          <>
            {/* Combined Platform Statistics */}
            {!statsLoading && stats && (
              <div className="mb-12">
                <div className="flex items-center mb-6">
                  <ChartBarIcon className="h-6 w-6 text-indigo-600 mr-2" />
                  <h2 className="text-2xl font-bold text-gray-800">
                    {t('admin.stats.title', 'Platform Statistics')}
                  </h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
                  <CombinedStatCard
                    title="QR Скенирања"
                    totalValue={stats.totalQrScans || 0}
                    icon={QrCodeIcon}
                    todayValue={stats.today?.qrScans || 0}
                    weekValue={stats.thisWeek?.qrScans || 0}
                    monthValue={stats.thisMonth?.qrScans || 0}
                    gradient="from-orange-500 to-orange-600"
                    to={`/${lang}/admin/qr-mapping`}
                    testId="admin-qr-codes-link"
                  />
                  <CombinedStatCard
                    title="Меморијални Профили"
                    totalValue={totalCounts.profiles}
                    icon={IdentificationIcon}
                    todayValue={stats.today.profilesCreated}
                    weekValue={stats.thisWeek.profilesCreated}
                    monthValue={stats.thisMonth.profilesCreated}
                    gradient="from-purple-500 to-purple-600"
                    to={`/${lang}/admin/profiles`}
                    testId="admin-view-all-profiles-link"
                  />
                  <CombinedStatCard
                    title="Корисници"
                    totalValue={totalCounts.users}
                    icon={UsersIcon}
                    todayValue={stats.today.newUsers}
                    weekValue={stats.thisWeek.newUsers}
                    monthValue={stats.thisMonth.newUsers}
                    gradient="from-green-500 to-green-600"
                    to={`/${lang}/admin/users`}
                    testId="admin-view-all-users-link"
                  />
                  <CombinedStatCard
                    title="Нарачки"
                    totalValue={totalCounts.orders}
                    icon={ShoppingBagIcon}
                    todayValue={stats.today.orders}
                    weekValue={stats.thisWeek.orders}
                    monthValue={stats.thisMonth.orders}
                    gradient="from-blue-500 to-blue-600"
                    to={`/${lang}/admin/orders`}
                    testId="admin-view-all-orders-link"
                  />
                  <CombinedStatCard
                    title="Прегледи на Профили"
                    totalValue={stats.totalProfileViews}
                    icon={EyeIcon}
                    todayValue={stats.today.profileViews}
                    weekValue={stats.thisWeek.profileViews}
                    monthValue={stats.thisMonth.profileViews}
                    gradient="from-indigo-500 to-indigo-600"
                    to={`/${lang}/admin/site-visits`}
                    testId="admin-site-visits-link"
                  />
                  <CombinedStatCard
                    title="Коментари"
                    totalValue={stats.totalComments || 0}
                    icon={ChatBubbleLeftEllipsisIcon}
                    todayValue={stats.today?.comments || 0}
                    weekValue={stats.thisWeek?.comments || 0}
                    monthValue={stats.thisMonth?.comments || 0}
                    gradient="from-teal-500 to-teal-600"
                    to={`/${lang}/admin/comments?tab=comments`}
                    testId="admin-comments-tile"
                  />
                  <CombinedStatCard
                    title="Пријави"
                    totalValue={stats.totalReports || 0}
                    icon={FlagIcon}
                    todayValue={stats.today?.reports || 0}
                    weekValue={stats.thisWeek?.reports || 0}
                    monthValue={stats.thisMonth?.reports || 0}
                    gradient="from-red-500 to-rose-600"
                    to={`/${lang}/admin/comments?tab=reports`}
                    testId="admin-reports-tile"
                  />
                  <CombinedStatCard
                    title="Контакт-пораки"
                    totalValue={totalCounts.contactMessages}
                    icon={EnvelopeIcon}
                    todayValue={0}
                    weekValue={0}
                    monthValue={0}
                    gradient="from-sky-500 to-cyan-600"
                    to={`/${lang}/admin/contact-messages`}
                    testId="admin-contact-messages-tile"
                  />
                </div>
                {notificationMetrics && <NotificationAdminCard />}
              </div>
            )}

            {/* Success Message */}
            <div className="mt-8 bg-green-50 border-l-4 border-green-400 p-4 rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-700">
                    {t('adminManagement.success', 'Admin access confirmed.')}
                  </p>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <svg className="animate-spin -ml-1 mr-3 h-12 w-12 text-indigo-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="text-xl font-medium text-gray-700">
                {t('adminManagement.loading', 'Verifying access...')}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminManagement;
